import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";

import { PLAYLIST } from "MVVM/GraphQL/playlist";

import {
  FEATURED_PRO_PLAYERS,
  PRO_PLAYER_CLIPS,
  PRO_TEAMS,
} from "MVVM/GraphQL/topPlayers";
import { getMapThumbnail } from "MVVM/Utilities/Clip";
import { getTagLabel } from "MVVM/Utilities/ClipTags";
import { parseProPlayer } from "MVVM/Views/Homepage/ClipOfTheDay/parseProPlayer";
import {
  FILTER_GROUPS,
  getClipTagValue,
  getPartnerUrl,
  IFilterGroupConfig,
  ITodayClip,
  TTodayError,
} from "MVVM/Views/AllstarToday/ViewModel";

export interface IProPlayer {
  avatar?: string | null;
  countryIso?: string | null;
  featured?: boolean | null;
  fullName?: string | null;
  gameIdentifier?: string | null;
  id: string;
  latestMatchDate?: string | null;
  name: string;
  team?: {
    id?: string | null;
    logo?: string | null;
    name?: string | null;
  } | null;
}

export interface IProTeam {
  id: string;
  logo?: string | null;
  name?: string | null;
  players: IProPlayer[];
}

export type TClipPeriod = "recent" | "all";
export type TFeedSort = "score" | "views" | "newest";

const SERVER_SORT: Record<TFeedSort, string> = {
  newest: "LATEST",
  score: "BEST",
  views: "VIEWS",
};

export type TTopPlayersTab = "clips" | "players";

// HLTV's image CDN refuses hotlinks (403); only Allstar-hosted copies render.
export const safeImage = (url?: string | null) =>
  url?.startsWith("http") && !url.includes("img-cdn.hltv.org")
    ? url
    : undefined;

// HLTV's "no photo" silhouette, re-hosted; about a third of rosters carry it.
// ponytail: one known hash; if HLTV changes the placeholder, add it here.
const PLACEHOLDER_AVATARS = [
  "662fa0efe40902f2ff78a5917e04e06403a9cb2743ef4143280a1f8a67139d14",
];

export const playerPhoto = (url?: string | null) => {
  const safe = safeImage(url);
  return safe && !PLACEHOLDER_AVATARS.some((hash) => safe.includes(hash))
    ? safe
    : undefined;
};

export const BEST_WINDOW_DAYS = 90;

export const PRO_FILTER_GROUPS: IFilterGroupConfig[] = [
  ...FILTER_GROUPS,
  { key: "opponent", label: "Opponent", tagKeys: ["PARTNER_opponentTeamName"] },
  { key: "event", label: "Event", tagKeys: ["PARTNER_eventName"] },
];

export const useFeaturedPros = () => {
  const { data, error, loading, refetch } = useQuery(FEATURED_PRO_PLAYERS);
  const pros: IProPlayer[] = data?.proPlayers || [];
  return { error: !!error, loading, pros, retry: refetch };
};

export const useProTeams = (skip: boolean) => {
  const { data, error, loading, refetch } = useQuery(PRO_TEAMS, { skip });
  const teams: IProTeam[] = data?.proTeams || [];
  return { error: !!error, loading, retry: refetch, teams };
};

// Rounded to the UTC day so the window is stable for the whole day and the
// server's per-day cache key is hit.
const bestWindowStart = () => {
  const now = new Date();
  return new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) -
      BEST_WINDOW_DAYS * 864e5,
  ).toISOString();
};

// One pro's clips, or a whole roster's when given several identifiers.
export const useProClips = (
  identifiers: string[],
  period: TClipPeriod,
  sort: TFeedSort,
) => {
  const createdAfter = useMemo(
    () => (period === "recent" ? bestWindowStart() : null),
    [period],
  );
  const { data, error, fetchMore, loading, networkStatus, refetch } = useQuery(
    PRO_PLAYER_CLIPS,
    {
      notifyOnNetworkStatusChange: true,
      skip: !identifiers.length,
      variables: {
        createdAfter,
        playerGameIdentifiers: identifiers,
        sort: SERVER_SORT[sort],
      },
    },
  );

  const connection = data?.clipsNew;
  const clips: ITodayClip[] = connection?.nodes || [];
  const loadingMore = networkStatus === 3;

  const loadMore = () => {
    if (!connection?.pageInfo?.hasNextPage || loadingMore) return;
    fetchMore({
      variables: { after: connection.pageInfo.endCursor },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          clipsNew: {
            ...fetchMoreResult.clipsNew,
            nodes: [...prev.clipsNew.nodes, ...fetchMoreResult.clipsNew.nodes],
          },
        };
      },
    });
  };

  return {
    clips,
    error: (error ? "fetch" : null) as TTodayError,
    hasMore: !!connection?.pageInfo?.hasNextPage,
    loading: loading && !loadingMore,
    loadingMore,
    loadMore,
    retry: refetch,
  };
};

export const getProMatchup = (clip: ITodayClip | null) => {
  const info = clip ? parseProPlayer(clip.tags) : null;
  if (!clip || !info?.opponentTeamName) return null;
  return {
    event: info.eventName,
    opponent: info.opponentTeamName,
    opponentLogo: safeImage(info.opponentTeamLogo),
    scoreLine: info.scoreLine,
    series: info.seriesGameLabel,
    eventLogo: safeImage(info.eventLogo),
    team: info.teamName,
    // the processed badge is our CDN copy of the team logo
    teamLogo:
      safeImage(getClipTagValue(clip, "PARTNER_badge")) ||
      safeImage(info.teamLogo),
  };
};

export const findPro = (
  id: string | undefined,
  featured: IProPlayer[],
  teams: IProTeam[],
) => {
  if (!id) return null;
  // Profile and clip links arrive with a steam id instead of the HLTV id.
  const matches = (pro: IProPlayer) =>
    pro.id === id || pro.gameIdentifier === id;
  return (
    featured.find(matches) ||
    teams.flatMap((team) => team.players).find(matches) ||
    null
  );
};

// The hourly HLTV top-lists job publishes the week's best scored clips as a
// playlist; `clips` (most viewed) is the fallback until bestClips is live.
export const useHltvTopClips = (skip: boolean) => {
  const [playlistId, setPlaylistId] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (skip) return undefined;
    let cancelled = false;
    setFailed(false);
    fetch(
      `${getPartnerUrl()}/cs/match/hltv/topList?entity=bestClips&entity=clips`,
    )
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((lists) => {
        if (cancelled) return;
        const id = (lists?.bestClips?.[0] || lists?.clips?.[0])?.playlist;
        if (id) setPlaylistId(id);
        else setFailed(true);
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [skip, attempt]);

  const { data, error, loading, refetch } = useQuery(PLAYLIST, {
    skip: skip || !playlistId,
    variables: { id: playlistId },
  });

  const clips: ITodayClip[] = useMemo(
    () =>
      [...(data?.playlist?.clips || [])].sort(
        (a, b) => (b.score || 0) - (a.score || 0),
      ),
    [data],
  );

  return {
    clips,
    error: (failed || error ? "fetch" : null) as TTodayError,
    loading: !skip && !failed && !error && (!playlistId || loading),
    retry: () => (failed ? setAttempt((n) => n + 1) : refetch()),
  };
};

// The mock's score tiers: the badge and score colour say how rare a clip is.
const TIERS: [number, string][] = [
  [285, "#C7DAF6"],
  [275, "#8582E9"],
  [265, "#D06AAE"],
  [255, "#4AB4A7"],
  [0, "#41A575"],
];

export const tierColor = (score?: number | null) =>
  TIERS.find(([min]) => (score ?? 0) >= min)?.[1] || TIERS[TIERS.length - 1][1];

const agoFormat = new Intl.RelativeTimeFormat("en", {
  numeric: "auto",
  style: "short",
});

export const ago = (iso?: string | null) => {
  if (!iso) return null;
  const days = Math.round((Date.parse(iso) - Date.now()) / 864e5);
  return Math.abs(days) < 30
    ? agoFormat.format(days, "day")
    : agoFormat.format(Math.round(days / 30), "month");
};

const compact = new Intl.NumberFormat("en", { notation: "compact" });

// The CDN names Dust 2's art "dust"; every other map matches its tag.
export const mapArt = (map: string) =>
  getMapThumbnail(/^dust\s*(2|ii)$/i.test(map) ? "dust" : map);

export const flagUrl = (iso: string) =>
  `${
    process.env.REACT_APP_S3_CLIP_BUCKET
  }/static/web/flags/${iso.toLowerCase()}.png`;

// Everything a card, row or modal shows about one pro clip.
export const clipFacts = (clip: ITodayClip) => {
  const matchup = getProMatchup(clip);
  const weapon = getClipTagValue(clip, "CS_Weapons");
  return {
    ago: ago(clip.createdAt),
    event: matchup?.event,
    map: getClipTagValue(clip, "CS_Map"),
    matchup,
    player: getClipTagValue(clip, "PARTNER_playerName") || clip.username || "",
    tier: tierColor(clip.score),
    views: clip.views ? compact.format(clip.views) : null,
    weapon: weapon ? getTagLabel("CS_Weapons", weapon) : null,
  };
};

export const playerStats = (clips: ITodayClip[]) => {
  const scores = clips.map((clip) => clip.score || 0);
  return {
    aces: clips.filter(
      (clip) =>
        getClipTagValue(clip, "CS_Situational")?.toLowerCase() === "ace",
    ).length,
    average: scores.length
      ? Math.round(scores.reduce((sum, s) => sum + s, 0) / scores.length)
      : 0,
    best: scores.length ? Math.max(...scores) : 0,
    count: clips.length,
  };
};

// HLTV pro ids trending this week (most viewed), from the public top-lists feed.
export const useTrendingProIds = (enabled: boolean) => {
  const [ids, setIds] = useState<string[]>([]);
  // Settled once the request finishes either way, so callers can wait on it.
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    if (!enabled || settled) return undefined;
    let cancelled = false;
    fetch(`${getPartnerUrl()}/cs/match/hltv/topList?entity=players`)
      .then((res) => (res.ok ? res.json() : null))
      .then((lists) => {
        if (cancelled) return;
        if (lists?.players)
          setIds(lists.players.map((p: { _id: string }) => String(p._id)));
        setSettled(true);
      })
      .catch(() => !cancelled && setSettled(true));
    return () => {
      cancelled = true;
    };
  }, [enabled, settled]);

  return { ids, settled };
};
